import { type Control, Controller } from 'react-hook-form';
import { InputSelect } from '@/components';
import type { Option } from '@/components/InputSelect/InputSelect';
import { SearchableGroupFilter } from '@/views/filter/SearchableGroupFilter';
import { useUserClientContext } from '@/app/clientContext';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import type { FilterState } from '@/features/filterSlice';
import {
  getNetworkGroupLabel,
  getFilterableGroups,
} from '@/helpers/networkGroups';
import { useFetchNetworkGroupsWithValuesQuery } from '@/features/networkGroupSlice';
import { useAppFlags } from '@/hooks/useAppFlags';

type GroupFiltersProps = {
  control: Control<FilterState>;
  disabled?: boolean;
};

export const GroupFilters = (props: GroupFiltersProps) => {
  const { context } = useUserClientContext();
  const intl = useClientTerminology();
  const { flagShiftSelect } = useAppFlags();
  const { data: networkGroups, isLoading } =
    useFetchNetworkGroupsWithValuesQuery();

  // TODO: what's the best practice here? a useEffect?
  if (!networkGroups || isLoading) return null;

  // Filter groups based on category field
  const filterableGroups = getFilterableGroups(networkGroups);

  return filterableGroups.map((group, i: number) => {
    const labelPluralMid = getNetworkGroupLabel(
      group,
      context,
      intl,
      'pluralInline'
    );

    const labelPluralStart = getNetworkGroupLabel(
      group,
      context,
      intl,
      'pluralTitle'
    );
    const groupPlaceholder = `All ${labelPluralMid}`;

    if (group.searchable) {
      return (
        <Controller
          key={`groupFilter${i}`}
          render={({ field: { onChange, onBlur, value } }) => (
            <SearchableGroupFilter
              groupId={group.id}
              initialValues={group.values ?? []}
              label={labelPluralStart}
              placeholder={groupPlaceholder}
              selected={value}
              onChange={onChange}
              onBlur={onBlur}
              disabled={props.disabled}
            />
          )}
          name={`groups.${group.id}`}
          control={props.control}
        />
      );
    }

    const moveEmptyToEnd = (groupNames: string[]): string[] => {
      const haveNames = groupNames.filter((str) => str !== '');
      const noNames = groupNames.filter((str) => str === '');
      return haveNames.concat(noNames);
    };

    const groupOptions = moveEmptyToEnd(group.values ?? []).map((v): Option => {
      if (v === '') {
        return {
          label: `No ${labelPluralMid}`,
          value: null,
          subLabel: `Spans with no known ${labelPluralMid}`,
        };
      } else {
        return { label: v, value: v };
      }
    });

    return (
      <Controller
        key={`groupFilter${i}`}
        render={({ field: { onChange, onBlur, value } }) => {
          return (
            <InputSelect
              data-testid={`group-filter-${group.id}`}
              label={labelPluralStart}
              isMulti={true}
              options={groupOptions}
              onChange={(value) => {
                onChange(value);
              }}
              placeholder={groupPlaceholder}
              value={groupOptions.filter((g) => value?.includes(g.value))}
              onBlur={onBlur}
              isDisabled={props.disabled}
              allowShiftSelect={flagShiftSelect}
            />
          );
        }}
        name={`groups.${group.id}`}
        control={props.control}
      />
    );
  });
};
